/** biome-ignore-all lint/style/noNonNullAssertion: null assertion rule doesn't matter here */
import {
	MutationCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider, useAuth } from "./auth.tsx";
import { DefaultCatchBoundary } from "./components/default-catch-boundary.tsx";
import { NotFound } from "./components/not-found.tsx";
import { routeTree } from "./routeTree.gen.ts";
import { isUnsupportedBrowser } from "./utils/browser-gate.ts";
import { initTelemetry } from "./utils/telemetry.ts";
import { useEffect } from "react";
import { MotionLoaderFullscreen } from "./components/custom/motion-loader.tsx";
import { m } from "./paraglide/messages.js";

// Recover from chunk-load failures (stale deploy or transient CDN 429s) by
// reloading once per session. The session flag prevents reload loops if the
// new bundle also fails to load — that error then bubbles to the catch
// boundary, which shows a "new version available" card.
const PRELOAD_RELOAD_KEY = "oneai:preload-reload";
window.addEventListener("vite:preloadError", (event) => {
	if (sessionStorage.getItem(PRELOAD_RELOAD_KEY)) {
		return;
	}
	sessionStorage.setItem(PRELOAD_RELOAD_KEY, "1");
	event.preventDefault();
	window.location.reload();
});
window.addEventListener("load", () => {
	setTimeout(() => sessionStorage.removeItem(PRELOAD_RELOAD_KEY), 10_000);
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 2 * 60 * 1000,
			gcTime: 30 * 60 * 1000,
			refetchOnWindowFocus: false,
			retry: 2,
			refetchOnMount: true,
			refetchOnReconnect: true,
		},
	},
	mutationCache: new MutationCache({
		onSuccess: () => {
			queryClient.invalidateQueries();
		},
	}),
});

// Set up a Router instance
const router = createRouter({
	routeTree,
	context: {
		queryClient,
		// auth will be passed down from InnerApp component
		auth: undefined!,
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	// Custom scroll containers (the app doesn't scroll the window — inner panels
	// do). Without this, scroll restoration carries a panel's scroll position over
	// to the next route and restores it, so a fresh page opens scrolled down.
	// Listing the container resets it to the top on forward navigation instead.
	scrollToTopSelectors: ["[data-scroll-reset]"],
	defaultErrorComponent: DefaultCatchBoundary,
	defaultPendingComponent: () => (
		<MotionLoaderFullscreen text={m.common_loading()} />
	),
	defaultNotFoundComponent: () => <NotFound />,
});

// Register things for typesafety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

function InnerRouterProvider() {
	const auth = useAuth();
	return <RouterProvider router={router} context={{ auth, queryClient }} />;
}

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<AuthProvider>
				<InnerRouterProvider />
			</AuthProvider>
		</QueryClientProvider>
	);
}

function Bootstrap() {
	useEffect(() => {
		// Fire and forget - telemetry loads in background
		initTelemetry();
	}, []);

	return <App />;
}

// Render the app — unless the minimum-browser gate already turned this browser
// away. Mounting anyway would run a broken app behind the interstitial.
const rootElement = document.getElementById("app")!;
if (!isUnsupportedBrowser() && !rootElement.innerHTML) {
	const root = createRoot(rootElement);
	root.render(
		<StrictMode>
			<Bootstrap />
		</StrictMode>,
	);
}
